import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

type Props = {
  children: React.ReactNode;
  labelClassName?: string;
} & React.ComponentPropsWithRef<'input'>;

export const Radio = React.forwardRef<HTMLInputElement, Props>(
  ({ children, labelClassName, checked, name, onChange, ...props }, ref) => {
    const isUncontrolled = checked === undefined;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [uncontrolledChecked, setUncontrolledChecked] = useState(false);

    useEffect(() => {
      if (!isUncontrolled || !name) return;
      const handler = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (target.type === 'radio' && target.name === name) {
          setUncontrolledChecked(inputRef.current?.checked ?? false);
        }
      };
      document.addEventListener('change', handler);
      return () => document.removeEventListener('change', handler);
    }, [isUncontrolled, name]);

    const visualChecked = isUncontrolled ? uncontrolledChecked : checked;

    const setRef = (el: HTMLInputElement | null) => {
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
    };

    const inputProps = isUncontrolled
      ? { ...props, name, onChange }
      : { ...props, name, checked, onChange };

    return (
      <label className={clsx('flex cursor-pointer items-center', labelClassName)}>
        <input ref={setRef} className="m-0 w-0 opacity-0" type="radio" {...inputProps} />
        <span
          className={clsx(
            'relative top-0 left-0 box-content block size-md rounded-[50%] border-2 border-solid border-border bg-white',
            'after:absolute after:top-[24%] after:right-[24%] after:block after:size-xs after:rounded-[50%] after:content-[""]',
            visualChecked ? 'border-primary! bg-primary! after:bg-white' : 'after:hidden'
          )}
        />
        <span className="ml-2 block text-sm">{children}</span>
      </label>
    );
  }
);

Radio.displayName = 'Radio';
